import { toaster } from '@/components/ui/toaster';
import { billingContextQueryKey } from '@/hooks/queries/use-billing-context-query';
import axiosInstance from '@/libs/axiosInstance';
import { isShopifyApprovalUrl } from '@/libs/billing-access';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import { isSuccessFeeTermsRefusal } from '@/libs/utils/success-fee-terms-refusal';
import type {
  ApiResponse,
  CreateOrganizationBillingChargeRequest,
  OrganizationBillingChargeResult,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { BillingTargetChangedError, useBillingActionScope } from './use-billing-action-scope';
import { trackProductAction } from '@/libs/product-analytics';
import { hasSiteMarketingConsent } from '@/libs/meta-pixel';
import { rememberSubscriptionIntent } from '@/libs/meta-pixel-subscription';

export const BILLING_CHARGE_MUTATION_KEY = ['billingCharge'] as const;

const checkoutUrl = z
  .string()
  .url()
  .refine((value) => new URL(value).protocol === 'https:');
const scopeFields = {
  organizationId: z.string().min(1),
  brandId: z.string().min(1),
  planId: z.enum(['free', 'essential_monthly', 'growth_monthly']),
};
const resultSchema = z.discriminatedUnion('kind', [
  z
    .object({
      ...scopeFields,
      kind: z.literal('redirect'),
      channel: z.enum(['shopify', 'stripe']),
      operationId: z.string().min(1).nullable(),
      url: checkoutUrl,
      expiresAt: z.string().datetime({ offset: true }).nullable(),
      // Paid Shopify responses retain these aliases for older dashboard clients.
      confirmationUrl: checkoutUrl.optional(),
      subscriptionId: z.string().min(1).optional(),
    })
    .strict(),
  z
    .object({
      ...scopeFields,
      kind: z.literal('pending'),
      channel: z.enum(['shopify', 'stripe']),
      operationId: z.string().min(1),
      retryAfterSeconds: z.number().finite().nonnegative(),
    })
    .strict(),
  z
    .object({
      ...scopeFields,
      kind: z.literal('applied'),
      channel: z.enum(['shopify', 'stripe']),
      operationId: z.string().min(1),
      subscriptionId: z.string().min(1),
    })
    .strict(),
]);
const legacySchema = z
  .object({ confirmationUrl: checkoutUrl, subscriptionId: z.string().min(1) })
  .strict();

export function useCreateBillingCharge() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { scope, assertCurrent } = useBillingActionScope();

  return useMutation<
    OrganizationBillingChargeResult,
    AxiosError<ApiResponse> | Error,
    CreateOrganizationBillingChargeRequest | string,
    typeof scope
  >({
    mutationKey: [...BILLING_CHARGE_MUTATION_KEY, scope.organizationId, scope.brandId],
    retry: false,
    onMutate: () => {
      assertCurrent(scope);
      return scope;
    },
    mutationFn: (input) =>
      trackProductAction('plans', 'subscribe', async () => {
        assertCurrent(scope);
        if (!scope.organizationId || !scope.brandId || !scope.userId)
          throw new Error('No billing target');
        const payload = typeof input === 'string' ? { planId: input } : input;
        const response = await axiosInstance.post<ApiResponse<unknown>>(
          '/dashboard/billing/charges',
          payload
        );
        assertCurrent(scope);
        if (response.data.success !== true) throw new Error('Billing charge was not accepted');

        // Older Shopify servers return the confirmation URL without a union.
        const legacy = legacySchema.safeParse(response.data.data);
        const parsed = resultSchema.safeParse(
          legacy.success
            ? {
                kind: 'redirect',
                channel: 'shopify',
                organizationId: scope.organizationId,
                brandId: scope.brandId,
                planId: payload.planId,
                operationId: null,
                url: legacy.data.confirmationUrl,
                expiresAt: null,
              }
            : response.data.data
        );
        if (
          !parsed.success ||
          parsed.data.organizationId !== scope.organizationId ||
          parsed.data.brandId !== scope.brandId ||
          parsed.data.planId !== payload.planId ||
          (parsed.data.planId === 'free' &&
            (parsed.data.channel !== 'shopify' ||
              !parsed.data.operationId ||
              (parsed.data.kind === 'redirect' && !isShopifyApprovalUrl(parsed.data.url)))) ||
          (parsed.data.kind !== 'redirect' &&
            parsed.data.channel === 'shopify' &&
            parsed.data.planId !== 'free') ||
          (parsed.data.kind === 'redirect' &&
            parsed.data.channel === 'stripe' &&
            (!parsed.data.operationId || !parsed.data.expiresAt))
        ) {
          throw new Error('Invalid billing charge response');
        }
        return parsed.data;
      }),
    onSuccess: async (data, _input, target) => {
      if (!target) throw new Error('Missing billing target');
      assertCurrent(target);
      await client.invalidateQueries({
        queryKey: billingContextQueryKey(target),
        exact: true,
      });
      assertCurrent(target);
      if (data.planId !== 'free' && hasSiteMarketingConsent()) {
        rememberSubscriptionIntent(data.organizationId, data.planId);
      }
      if (data.kind === 'redirect') window.location.href = data.url;
    },
    onError: (error) => {
      if (error instanceof BillingTargetChangedError || isSuccessFeeTermsRefusal(error)) return;
      toaster.error({
        title: t('common.error'),
        description: getApiErrorMessage(error, t, 'pricing.errors.billingChargeFailed'),
        duration: 5000,
      });
    },
  });
}
